import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import useGetRequest from "hooks/useGetRequest";
import Table from "component/table";
import Pagination from "component/pagination";
import Input from "component/form/Input";
import capitalizeWords from "utilities/capitalizeWords";
import capitalizeFirstLetter from "utilities/capitalizeFirstLetter";
import currencyPairCheck from "utilities/currencyPairCheck";
import { formatMoney } from "utilities/formatMoney";
import useDebounce from "hooks/useDebounce";
import notFoundImg from "asset/images/not-found.svg";
import { businessToUse } from "component/flex/utils";
import { SearchIcon } from "asset/icons";
import MoreActions from "./MoreActions";
import FilterBtn from "./FilterBtn";
import Status from "./Status";
import { trackFeatureUsage } from "utilities/trackFeatureUsage";
import TableSearchInput from "component/TableSearchInput";
dayjs.extend(utc);

const ProcurementTable = ({ quotesTab, business, currency, loadedPoDataRef }) => {
    const [fetchInboundOrders, inboundOrders, inboundOrdersLoading] = useGetRequest();
    const [fetchOutboundOrders, outboundOrders, outboundOrdersLoading] = useGetRequest(false);
    const [fetchCompletedOrders, completedOrders, completedOrdersLoading] = useGetRequest(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusTypes, setStatusTypes] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [updatedStatusOrders, setUpdatedStatusOrders] = useState({});
    const [fetchWallets, wallets] = useGetRequest();
    const [currentPage, setCurrentPage] = useState(1);
    const tabsArr = quotesTab ? ["Inbound", "Outbound"] : ["Inbound", "Outbound", "Completed"];
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const isBusinessApproved = business?.status === "APPROVED";

    const tabFromURL = queryParams.get("tab") || "Inbound";
    const [activeTab, setActiveTab] = useState(tabFromURL);

    useEffect(() => {
        setActiveTab(tabFromURL);
    }, [tabFromURL]);

    const sortByDate = (arr, key = "deliveryDate") =>
        arr?.sort((a, b) => {
            let dateA = new Date(a[key]),
                dateB = new Date(b[key]);
            return dateA - dateB;
        });

    const tabsData = useMemo(() => {
        const loaded = loadedPoDataRef.current;
        return {
            Inbound: inboundOrders
                ? { ...inboundOrders, inbound: sortByDate(inboundOrders.inbound) }
                : loaded?.Inbound,
            Outbound: outboundOrders
                ? { ...outboundOrders, outBound: sortByDate(outboundOrders.outBound) }
                : loaded?.Outbound,
            Completed: completedOrders
                ? {
                      ...completedOrders,
                      completedOrders: sortByDate(completedOrders.completedOrders),
                  }
                : loaded?.Completed,
        };
    }, [inboundOrders, outboundOrders, completedOrders]);

    const tabList = tabsData[activeTab]
        ? Object.values(tabsData[activeTab]).find((item) => Array.isArray(item))
        : null;

    const loading =
        (searchTerm || statusTypes.length || startDate || endDate || !tabList) &&
        (inboundOrdersLoading || outboundOrdersLoading || completedOrdersLoading);

    const completedType = (order) => {
        const type = order.inbound.businessId === business._id ? "inbound" : "outbound";

        return (
            <span
                className={`text-sm !px-2 rounded-[13px] font-normal ${
                    type === "inbound"
                        ? "bg-[#DCFDDB] text-[#107C10]"
                        : "bg-[#FFEFC6] text-[#B64607]"
                }`}
            >
                {capitalizeFirstLetter(type)}
            </span>
        );
    };

    const viewOrder = (orderId) =>
        navigate(`/flex/home/manage-po?orderId=${orderId}&type=${activeTab.toLowerCase()}`);

    useEffect(() => {
        business?._id && fetchWallets("/v1/wallets/accounts");
    }, [business?._id]);

    useEffect(() => {
        if (business?._id) {
            const queryString = `searchTerm=${searchTerm}&limit=10&page=${currentPage}&currency=${currency}&startDate=${startDate}&endDate=${endDate}${statusTypes
                .map((type) => `&statuses=${type}`)
                .join("")}`;
            if (activeTab === "Inbound")
                fetchInboundOrders(`/v1/flex/order/inbounds-orders?${queryString}`, {}, false);

            if (activeTab === "Outbound")
                fetchOutboundOrders(`/v1/flex/order/outbounds-orders?${queryString}`, {}, false);

            if (activeTab === "Completed")
                fetchCompletedOrders(`/v1/flex/order/completed-orders?${queryString}`, {}, false);
        }
    }, [
        business?._id,
        activeTab,
        searchTerm,
        currency,
        statusTypes,
        startDate,
        endDate,
        currentPage,
    ]);

    useEffect(() => {
        return () => {
            loadedPoDataRef.current = { ...loadedPoDataRef.current, ...tabsData };
        };
    }, [tabsData]);

    useEffect(() => {
        quotesTab && activeTab === "Completed" && setActiveTab("Inbound");
    }, [activeTab, quotesTab]);

    const updateSearchString = useDebounce(async (searchString) => {
        !loading && setSearchTerm(searchString.trim().toLowerCase());
    }, 700);

    return (
        <div className="bg-white max-sm:!border-x-0 h-min !border !border-[#E4E7EC] sm:rounded-xl !mb-10">
            <div className="flex xl:items-center max-xl:flex-col !px-4 sm:!px-4 !py-6">
                <div className="bg-[#EAECF0] rounded-lg !p-1 flex !gap-2 w-full 820:w-max">
                    {tabsArr.map((tab) => (
                        <button
                            className={`rounded w-[166px] h-[34px] font-medium text-[13px] 330:text-sm ${
                                activeTab === tab ? "bg-white text-main-dark" : "text-main-grey"
                            }`}
                            key={tab}
                            onClick={() => {
                                setSearchTerm("");
                                setActiveTab(tab);
                                setCurrentPage(1);
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div className="flex items-center justify-between md:justify-end max-xl:mt-4 xl:ml-auto">
                    <TableSearchInput />
                    <FilterBtn
                        {...{
                            endDate,
                            startDate,
                            statusTypes,
                            setEndDate,
                            setStartDate,
                            setStatusTypes,
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <Bars
                    wrapperClass="w-max mx-auto mt-[100px] mb-[160px]"
                    height={30}
                    width={30}
                    color="#6F00FF"
                />
            ) : (
                <>
                    {!tabList?.length ? (
                        <div className="flex flex-col items-center !mt-9 !mb-11">
                            <img
                                src={notFoundImg}
                                alt={`No ${activeTab} Orders`}
                                className="w-[144px] h-[107.39px]"
                            />
                            <p className="text-main-dark font-medium text-sm !mt-5 !mb-[22px]">
                                You don’t have any {activeTab.toLowerCase()} purchase orders
                            </p>
                            <button
                                className={`bg-[#5E01D6] !py-2 !px-4 rounded-lg !text-[#FBFAFF] 
        ${!isBusinessApproved ? "opacity-50 cursor-not-allowed" : ""}`}
                                onClick={() => {
                                    if (isBusinessApproved) {
                                        trackFeatureUsage("create_po", business?._id || "guest");
                                        navigate("/flex/create-po");
                                    }
                                }}
                                disabled={!isBusinessApproved}
                            >
                                Create a Purchase Order
                            </button>
                        </div>
                    ) : (
                        <>
                            <Table
                                className="overflow-hidden"
                                titles={[
                                    { title: "Due date", width: "170px" },
                                    { title: "Order number", width: "186px" },
                                    {
                                        title:
                                            activeTab === "Completed"
                                                ? "Partner"
                                                : activeTab === "Inbound"
                                                ? "Buyer"
                                                : "Supplier",
                                        width: "240px",
                                    },
                                    { title: "Value", width: "220px" },
                                    {
                                        title: activeTab === "Completed" ? "Type" : "Status",
                                        width: "240px",
                                    },
                                    { title: "", width: "minmax(100px, 1fr)" },
                                ]}
                                rows={tabList.map((order, idx) => ({
                                    columns: [
                                        {
                                            value: order.deliveryDate
                                                ? dayjs(order.deliveryDate)
                                                      .utc()
                                                      .format("MMMM D, YYYY")
                                                : "No due date",

                                            className: "text-[#475467] font-normal",
                                        },
                                        {
                                            value: (
                                                <span className="w-full truncate text-main-dark">
                                                    {order?.title}
                                                </span>
                                            ),
                                        },
                                        {
                                            value: (
                                                <>
                                                    {capitalizeWords(
                                                        businessToUse(
                                                            order,
                                                            business,
                                                            activeTab.toLowerCase()
                                                        ).bizName
                                                    )}
                                                    <span className="block text-[#475467] font-normal">
                                                        {capitalizeFirstLetter(
                                                            businessToUse(
                                                                order,
                                                                business,
                                                                activeTab.toLowerCase()
                                                            ).name ??
                                                                businessToUse(
                                                                    order,
                                                                    business,
                                                                    activeTab.toLowerCase()
                                                                ).email
                                                        )}
                                                    </span>
                                                </>
                                            ),
                                            className: "flex flex-col items-start text-main-dark",
                                        },
                                        {
                                            value: `${currencyPairCheck(
                                                order.currency
                                            )} ${formatMoney(order.totalAmount)}`,
                                            className: "text-[#475467] font-normal",
                                        },
                                        {
                                            value:
                                                activeTab === "Completed" ? (
                                                    completedType(order)
                                                ) : (
                                                    <Status
                                                        status={
                                                            updatedStatusOrders[order._id]?.tab ===
                                                            activeTab
                                                                ? updatedStatusOrders[order._id]
                                                                      .status
                                                                : order.orderStatus.toLowerCase()
                                                        }
                                                    />
                                                ),
                                        },
                                        {
                                            value: (
                                                <>
                                                    <span
                                                        onClick={(evt) => {
                                                            evt.stopPropagation();
                                                            viewOrder(order._id);
                                                        }}
                                                    >
                                                        View
                                                    </span>

                                                    <MoreActions
                                                        orderId={order._id}
                                                        order={order}
                                                        tab={activeTab.toLowerCase()}
                                                        updateOrder={(status) =>
                                                            setUpdatedStatusOrders((prev) => ({
                                                                ...prev,
                                                                [order._id]: {
                                                                    tab: activeTab,
                                                                    status: status.toLowerCase(),
                                                                },
                                                            }))
                                                        }
                                                        status={order?.orderStatus?.toLowerCase()}
                                                        disableCancel={
                                                            order?.orderStatus === "CANCELLED" ||
                                                            activeTab !== "Outbound" ||
                                                            updatedStatusOrders[order._id]
                                                                ?.status === "cancelled"
                                                        }
                                                        currency={order.currency}
                                                        wallets={wallets}
                                                        totalAmount={order?.totalAmount}
                                                        dropClassName={`${
                                                            (tabList.length - 1 === idx ||
                                                                tabList.length - 2 === idx ||
                                                                tabList.length - 3 === idx) &&
                                                            ""
                                                        }`}
                                                    />
                                                </>
                                            ),
                                            className:
                                                "flex text-[#1D2939] font-[450] text-sm !gap-3",
                                        },
                                    ],
                                    rowId: order._id,
                                    onRowClick: () => viewOrder(order._id),
                                    className:
                                        tabList.length - 1 === idx &&
                                        tabsData[activeTab].totalPages > 1 &&
                                        "border-b",
                                }))}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                totalPages={tabsData[activeTab].totalPages}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ProcurementTable;
